/**
 * @author Karthik <karthik.x@314ecorp.com>
 * @description Entry for web app
 */

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import posthog from 'posthog-js';
import { BrowserRouter, Routes, Route, matchPath } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import { preconnect } from 'react-dom';

import './boot/fabric';
import './boot/faro';
import config from './config';
import space from './features/spaces/JeevesSpace';
import { appRoutes, routes } from './utils/menuConfig';

const Meeting = React.lazy(() => import('./features/meeting/Meeting'));
const ExternalPlayer = React.lazy(() => import('./features/external-player/ExternalPlayer'));
const EmbeddedLink = React.lazy(() => import('./features/embedded/EmbeddedLink'));
const Entry = React.lazy(() => import('./Entry'));

preconnect(config.client.url, { crossOrigin: 'anonymous' });
preconnect(config.keycloak.url, { crossOrigin: 'anonymous' });

posthog.init(config.posthog.apiKey, config.posthog.options);
posthog.register({ version: APP_VERSION });
posthog.group('tenant', config.tenant, { name: config.tenant });

const basePath = (window.basePath ?? '').slice(0, -1);
const pathname = window.location.pathname;
const possibleCode = matchPath('/:spaceCode/*', pathname.replace(basePath, ''))?.params.spaceCode;
const spaceCode = appRoutes.includes(possibleCode) ? null : possibleCode;

if (spaceCode) {
	// store space name in session storage which is consumed at space initialization time
	space.setSpace(spaceCode);
} else {
	// Legacy support: Add space name prefix to bookmarks and saved url
	const url = new URL(window.location.href);
	url.pathname = space.createBasePath().slice(0, -1) + url.pathname.replace(basePath, '');
	window.history.pushState({}, '', url.toString());
}

const baseName = `${basePath}/${spaceCode ?? space.getSpace().code}`;
const root = document.getElementById('root') ?? document.body;

const JSX = (
	<PostHogProvider client={posthog}>
		<BrowserRouter basename={baseName}>
			<Suspense fallback={<div>Loading...</div>}>
				<Routes>
					<Route path={routes.meetings} element={<Meeting />} />
					<Route path={routes.player} element={<ExternalPlayer />} />
					<Route path={routes.embedded} element={<EmbeddedLink />} />
					<Route path={routes.root} element={<Entry />} />
				</Routes>
			</Suspense>
		</BrowserRouter>
	</PostHogProvider>
);

ReactDOM.createRoot(root).render(JSX);
